import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { RelocationRestService } from 'app/core/services/rest/relocation.rest.service';
import { Observable, of } from 'rxjs';
import { RootState } from '..';
import { AzureMonitoringService } from '../../services/AzureMonitoringService';
import { OrderRestService } from '../../services/rest/order.rest.service';
import { TemplateRestService } from '../../services/rest/template.rest.service';
import { AbstractEffects } from '../AbstractEffects';
import * as actions from './template.actions';
import { TemplateCreateFromRelocationStartAction, TemplateCreateFromRelocationSuccessAction, TemplatesDeleteAction, TemplatesLoadAction, TemplatesStartEditAction, TemplatesStartEditSuccessAction } from './template.actions';
import { goAction } from '../router/router.actions';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';

@Injectable({ providedIn: 'root' })
export class TemplateEffects extends AbstractEffects {
  copyRelocationForTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<TemplateCreateFromRelocationStartAction>(actions.TEMPLATE_COPY_FROM_RELOCATION),
      withLatestFrom(this.store, (action: TemplateCreateFromRelocationStartAction, state) => {
        return { payload: action.payload, state };
      }),
      switchMap(({ payload, state }) => {
        return this.relocationRestService.getRelocation(payload.etmOrderNumber, null).pipe(
          map(result => {
            return new actions.TemplateCreateFromRelocationSuccessAction({
              relocationDetail: result,
              definitions: state.definitions,
            });
          }),
          catchError(err => this.handleFail(err, 'ERRORS.TEMPLATE.COPY_FROM_ORDER', []))
        );
      })
    )
  );
  copyRelocationRequestDeliveryDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<TemplateCreateFromRelocationSuccessAction>(actions.TEMPLATE_COPY_FROM_RELOCATION_SUCCESS),
      switchMap(() => {
        return of(goAction({ path: '/relocation/template/copy' }));
      })
    )
  );
  loadTemplates$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TEMPLATES_LOAD),
      debounceTime(50), // Avoid double click
      switchMap((action: TemplatesLoadAction) =>
        this.templateRestService.getTemplates(action.payload.type).pipe(
          map(templateArray => new actions.TemplatesLoadSuccessAction(templateArray)),
          catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.TemplatesLoadFailAction(err))]))
        )
      )
    )
  );
  deleteTemplate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TEMPLATES_DELETE),
      switchMap((action: TemplatesDeleteAction) => {
        return this.templateRestService.deleteTemplate(action.payload.type, action.payload.templateId).pipe(
          map(result => new actions.TemplatesDeleteSuccessAction(result)),
          catchError(err => this.handleFail(err, 'ERRORS.TEMPLATE.CREATE', [of(new actions.TemplatesDeleteFailAction(err))]))
        );
      })
    )
  );
  startOrderEditTemplate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.TEMPLATES_START_EDIT),
      switchMap((action: TemplatesStartEditAction) =>
        this.templateRestService.getTemplate(action.payload.type, action.payload.templateId).pipe(
          map(template => new actions.TemplatesStartEditSuccessAction({ template, type: action.payload.type })),
          catchError(err => this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.TemplatesStartEditFailAction(err))]))
        )
      )
    )
  );
  startEditSuccessTemplate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<TemplatesStartEditSuccessAction>(actions.TEMPLATES_START_EDIT_SUCCESS),
      withLatestFrom(this.store, (action: TemplatesStartEditSuccessAction, state) => {
        return { payload: action.payload, state };
      }),
      switchMap(({ payload, state }) => {
        const newActions = [];
        switch (payload.type) {
          case 'ORDER':
            const customer = state.session.selectedCustomer.data;
            newActions.push(goAction({ path: `/orders/template/edit/${payload.template.id}` }));
            newActions.push(
              new actions.OrderTemplateEditAction({
                template: payload.template,
                definitions: state.definitions,
                customer,
              })
            );
            break;
          case 'RELOCATION':
            newActions.push(goAction({ path: `/relocation/template/edit/${payload.template.id}` }));
            newActions.push(new actions.RelocationTemplateEditAction({ template: payload.template }));
            break;
        }
        return newActions;
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private templateRestService: TemplateRestService,
    private orderRestService: OrderRestService,
    private relocationRestService: RelocationRestService,
    translateService: TranslateService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
